import _ from 'lodash'
import siteConfig from './site-config.json'
import * as logger from '../abt-utils/logger'
import * as pageData from '../abt-utils/page-data'

var mod = {}

/** Initializes the client data module */
mod.init = function init () {
  var data = mod._purge(mod.get(), _.keys(siteConfig.clientData.categories))

  if (!mod._page()) return

  var currentCat = mod._category(_.keys(siteConfig.clientData.categories))

  if (currentCat) {
    mod._record(data, currentCat)
  }
}

/**
 * Gets the client data stored in local storage.
 * @returns {object} the client data, or an empty object if there is no data.
 */
mod.get = function get () {
  var data = {}

  try {
    data = JSON.parse(window.localStorage[siteConfig.clientData.location])
  } catch (error) {
    logger.error(error)
  }

  return data
}

/**
 * Sets the client data in local storage
 * @param {object} data - the data to store.
 */
mod.set = function set (data) {
  window.localStorage[siteConfig.clientData.location] = JSON.stringify(data)
}

/**
 * Purges old and extraneous data from local storage.
 * @param {object} data - the client data.
 * @param {array} categories - the categories for which data should be kept.
 * @returns {object} the purged data.
 */
mod._purge = function _purge (data, categories) {
  if (!data || _.isEmpty(data)) return

  var cutoff = mod._cutoff(siteConfig.clientData.duration)

  mod._purgeCategories(data, categories)
  mod._purgeByDate(data, cutoff, 'visits')
  mod._purgeEmpty(data, 'visits')
  mod.set(data)

  return data
}

/**
 * Removes untracked categories.
 * @param {object} data - the client data.
 * @param {array} categories - the categories for which data should be kept.
 */
mod._purgeCategories = function _purgeCategories (data, categories) {
  var storedCategories = _.keys(data)
  var untracked = _.without.apply({}, [storedCategories].concat(categories))
  _.map(untracked, function (category) {
    delete data[category]
  })
}

/**
 * Removes anachronistic data.
 * @param {object} data - the client data.
 * @param {object} cutoff - the formatted date to which each date will be compared.
 * @param {string} metric - the property to purge dates from (e.g. 'visits').
 */
mod._purgeByDate = function _purgeByDate (data, cutoff, metric) {
  var today = parseInt(mod._date(new Date()), 10)

  function extraneousDates (date) {
    date = parseInt(date, 10)
    var isOld = cutoff > 0 && date < cutoff
    return isOld || date > today
  }

  function deleteData (categoryData, category) {
    var subject = categoryData[metric]
    if (!subject) return

    var dates = _.chain(subject).keys().filter(extraneousDates).value()
    _.map(dates, function (date) {
      delete data[category][metric][date]
    })
  }

  _.forOwn(data, deleteData)
}

/**
 * Removes empty properties.
 * @param {object} data - the client data.
 * @param {string} metric - the property to purge (e.g. 'visits').
 */
mod._purgeEmpty = function _purgeEmpty (data, metric) {
  function deleteEmpty (categoryData, category) {
    if (_.isEmpty(categoryData[metric])) {
      delete data[category][metric]
    }
    if (_.isEmpty(categoryData)) {
      delete data[category]
    }
  }

  _.forOwn(data, deleteEmpty)
}

/**
 * Checks if there is content from one of the qualifying categories.
 * @param {array} categories - the list of qualifying categories.
 * @returns {string} the category that qualified, or undefined.
 */
mod._category = function _category (categories) {
  var category
  var catLevels = [
    'catId',
    'parentCatId',
    'uberCatId'
  ]
  _.find(catLevels, function (catLevel) {
    var property = pageData.get(catLevel)
    category = _.find(categories, function (item) {
      return _.includes(property, item)
    })
    return category
  })

  if (!category) {
    logger.log('ABT: no data recorded (reason: unqualified category)')
  }

  return category
}

/**
 * Initializes or increments the visit counter for the date of the visit.
 * @param {object} data - the data that will be recorded.
 * @param {string} currentCat - the category id to record the visit for.
 */
mod._record = function _record (data, currentCat) {
  data = data || {}
  var date = mod._date(new Date())

  if (!data[currentCat] || !data[currentCat].visits) {
    data[currentCat] = { visits: {} }
  }

  if (!data[currentCat].visits[date]) {
    data[currentCat].visits[date] = 0
  }

  var visitCount = data[currentCat].visits[date]
  data[currentCat].visits[date] = parseInt(visitCount, 10) + 1
  mod.set(data)

  var message = 'ABT: visit recorded for ' + currentCat
  logger.info([message, data])
}

/**
 * Determines if data should be recorded for the current page.
 * @returns {string} the page type, or undefined.
 */
mod._page = function _page () {
  var result = _.includes(siteConfig.clientData.pages, pageData.get('pageType'))
  if (!result) {
    logger.log('ABT: no data recorded (reason: unqualified page)')
  }

  return result
}

/**
 * Formats a date into a string of four numbers representing GMT-0 day and month.
 * @param {object} dateObj - the js Date object to format.
 * @returns {string} the formatted date (MMDD).
 */
mod._date = function _date (dateObj) {
  var dateString = _.first(dateObj.toISOString().split('T'))
  var parts = dateString.split('-')
  parts.shift()
  return parts.join('')
}

/**
 * Gets the integer representing the cutoff date.
 * @param {int} duration - the number of days the data will be kept.
 * @returns {int} the cutoff date.
 */
mod._cutoff = function _cutoff (duration) {
  var cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - duration)
  cutoff = parseInt(mod._date(cutoff), 10)
  return (cutoff < mod._date(new Date())) ? cutoff : 0
}

export default mod



/*****************
 ** WEBPACK FOOTER
 ** ./src/abt-core/client-data.js
 ** module id = 43
 ** module chunks = 2
 **/